interface FrameRateData {
  fps: number;
  timestamp: number;
}
type PerformanceLevel = 'high' | 'medium' | 'low';

interface PerformanceThresholds {
  high: number;
  medium: number;
  low: number;
}

export class PerformanceController {
  private isMobile: boolean;
  private devicePerformanceLevel: PerformanceLevel;
  private recommendedFrameRate: number;
  private performanceThresholds: PerformanceThresholds;

  private frameCount: number;
  private lastFrameTime: number;
  private recentFrameRates: FrameRateData[];
  private frameRateCheckInterval: number;
  private lastFrameRateCheck: number;
  private frameRateDropThreshold: number;
  private enableLowFrameRateMode: boolean;
  private lastRecoveryTime: number;
  private recoveryProtectionDelay: number;
  private monitoringStartTime: number;
  private monitoringDelay: number;
  private isMonitoringEnabled: boolean;
  private isUserEnablLowFrameRateMode: boolean;

  constructor(isMobile = false) {
    // 设备性能检测相关属性
    this.isMobile = isMobile;
    this.devicePerformanceLevel = isMobile ? 'medium' : 'high'; // PC默认高性能，移动端默认中等性能
    this.recommendedFrameRate = isMobile ? 90 : 120; // PC默认120fps，移动端默认90fps
    this.performanceThresholds = {
      high: 120,
      medium: 90,
      low: 60,
    };
    // 帧率监控相关属性
    this.frameCount = 0;
    this.lastFrameTime = Date.now();
    this.recentFrameRates = []; // 存储最近的帧率数据
    this.frameRateCheckInterval = 1000; // 每秒检查一次帧率
    this.lastFrameRateCheck = Date.now();
    this.frameRateDropThreshold = 0.3; // 30%的帧率下降阈值
    this.enableLowFrameRateMode = false; // 是否启用低帧率模式的开关
    this.lastRecoveryTime = 0; // 上次恢复低帧率模式的时间
    this.recoveryProtectionDelay = 10000; // 恢复后10秒内不再进入低帧率模式
    this.monitoringStartTime = 0; // 开始监测的时间
    this.monitoringDelay = 10000; // 进入场景后10秒开始监测
    this.isMonitoringEnabled = false; // 是否启用帧率监测
    this.isUserEnablLowFrameRateMode = false;
  }
  // 检测设备性能
  detectDevicePerformance(): void {
    // PC设备默认为高性能，无需检测
    if (!this.isMobile) {
      this.devicePerformanceLevel = 'high';
      this.recommendedFrameRate = this.performanceThresholds.high;
      console.log('PC设备默认性能等级：高，推荐帧率：120fps');
      return;
    }
    // 移动端设备性能检测
    try {
      const canvas = document.createElement('canvas');
      const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as WebGLRenderingContext;

      if (!gl) {
        this.devicePerformanceLevel = 'low';
        this.recommendedFrameRate = this.performanceThresholds.low;
        console.log('WebGL不支持，设备性能等级：低');
        return;
      }
      // 获取GPU信息
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      let gpuInfo = '';
      if (debugInfo) {
        gpuInfo = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL).toLowerCase();
      }
      // 获取设备内存信息（如果可用）
      const memory = navigator.deviceMemory || 4;
      // 获取CPU核心数
      const cores = navigator.hardwareConcurrency || 4;
      // 性能评分算法
      let performanceScore = 0;
      // GPU评分（基于常见移动GPU）
      if (gpuInfo.includes('adreno')) {
        if (gpuInfo.includes('740') || gpuInfo.includes('730') || gpuInfo.includes('720')) {
          performanceScore += 40; // 高端Adreno
        } else if (gpuInfo.includes('650') || gpuInfo.includes('640') || gpuInfo.includes('630')) {
          performanceScore += 30; // 中端Adreno
        } else {
          performanceScore += 20; // 低端Adreno
        }
      } else if (gpuInfo.includes('mali')) {
        if (gpuInfo.includes('g78') || gpuInfo.includes('g77') || gpuInfo.includes('g76')) {
          performanceScore += 35; // 高端Mali
        } else if (gpuInfo.includes('g72') || gpuInfo.includes('g71') || gpuInfo.includes('g52')) {
          performanceScore += 25; // 中端Mali
        } else {
          performanceScore += 15; // 低端Mali
        }
      } else if (gpuInfo.includes('apple')) {
        performanceScore += 45; // Apple GPU通常性能较好
      } else {
        performanceScore += 20; // 未知GPU，给中等分数
      }
      // 内存评分
      if (memory >= 8) {
        performanceScore += 30;
      } else if (memory >= 6) {
        performanceScore += 25;
      } else if (memory >= 4) {
        performanceScore += 20;
      } else {
        performanceScore += 10;
      }
      // CPU核心数评分
      if (cores >= 8) {
        performanceScore += 20;
      } else if (cores >= 6) {
        performanceScore += 15;
      } else if (cores >= 4) {
        performanceScore += 10;
      } else {
        performanceScore += 5;
      }
      // 根据总分确定性能等级
      if (performanceScore >= 70) {
        this.devicePerformanceLevel = 'high';
        this.recommendedFrameRate = this.performanceThresholds.high;
      } else if (performanceScore >= 40) {
        this.devicePerformanceLevel = 'medium';
        this.recommendedFrameRate = this.performanceThresholds.medium;
      } else {
        this.devicePerformanceLevel = 'low';
        this.recommendedFrameRate = this.performanceThresholds.low;
      }

      console.log('设备性能检测完成：');
      console.log(`- 设备得分: ${performanceScore}`);
      console.log(`- 性能等级: ${this.devicePerformanceLevel}`);
      console.log(`- 推荐帧率: ${this.recommendedFrameRate}fps`);
      console.log(`- 性能评分: ${performanceScore}`);
      console.log(`- GPU信息: ${gpuInfo}`);
      console.log(`- 内存: ${memory}GB`);
      console.log(`- CPU核心: ${cores}`);
    } catch (error) {
      console.warn('设备性能检测失败，使用默认中等性能设置:', error);
      this.devicePerformanceLevel = 'medium';
      this.recommendedFrameRate = this.performanceThresholds.medium;
    }
  }
  // 开始帧率监测
  startMonitoring(): void {
    this.monitoringStartTime = Date.now();
    this.isMonitoringEnabled = false; // 初始不启用，等待延迟时间
    console.log('帧率监测将在10秒后开始');
  }
  // 监控帧率变化
  monitorFrameRate(): void {
    const currentTime = Date.now();
    // 检查是否到了开始监测的时间
    if (!this.isMonitoringEnabled && this.monitoringStartTime > 0) {
      if (currentTime - this.monitoringStartTime >= this.monitoringDelay) {
        this.isMonitoringEnabled = true;
        console.log('开始帧率监测');
      } else {
        // 还没到监测时间，只计数不分析
        this.frameCount = this.frameCount + 1;
        return;
      }
    }
    // 如果监测未启用，直接返回
    if (!this.isMonitoringEnabled) {
      this.frameCount = this.frameCount + 1;
      return;
    }

    this.frameCount = this.frameCount + 1;
    // 每秒计算一次帧率
    if (currentTime - this.lastFrameRateCheck >= this.frameRateCheckInterval) {
      const fps = this.frameCount / ((currentTime - this.lastFrameRateCheck) / 1000);
      this.recentFrameRates.push({
        fps,
        timestamp: currentTime,
      });
      // 只保留最近4秒的数据（用于计算1秒和3秒的平均值）
      this.recentFrameRates = this.recentFrameRates.filter((data) => currentTime - data.timestamp <= 4000);
      // 检查是否需要启用低帧率模式
      this.checkFrameRateChange();
      // 重置计数器
      this.frameCount = 0;
      this.lastFrameRateCheck = currentTime;
    }
  }
  // 检查帧率变化并决定是否启用低帧率模式
  checkFrameRateChange(): void {
    if (this.recentFrameRates.length < 4) return; // 需要至少4秒的数据

    const currentTime = Date.now();
    // 计算最近1秒的平均帧率
    const recent1SecData = this.recentFrameRates.filter((data) => currentTime - data.timestamp <= 1000);
    const avg1Sec = recent1SecData.reduce((sum, data) => sum + data.fps, 0) / recent1SecData.length;
    // 计算之前3秒的平均帧率（排除最近1秒）
    const previous3SecData = this.recentFrameRates.filter(
      (data) => currentTime - data.timestamp > 1000 && currentTime - data.timestamp <= 4000,
    );
    const avg3Sec = previous3SecData.reduce((sum, data) => sum + data.fps, 0) / previous3SecData.length;

    if (avg3Sec > 0) {
      const frameRateRatio = avg1Sec / avg3Sec;
      const frameRateDrop = 1 - frameRateRatio;
      // 计算推荐帧率的30%阈值
      const recommendedFrameRateThreshold = this.recommendedFrameRate * (1 - this.frameRateDropThreshold);
      const isFrameRateBelowRecommended = avg1Sec < recommendedFrameRateThreshold;
      // 如果3s平均帧率小于推荐帧率的30%
      const isAvg3SecBelowRecommended = avg3Sec < recommendedFrameRateThreshold;
      // 检查是否在恢复保护期内
      const isInRecoveryProtection = currentTime - this.lastRecoveryTime < this.recoveryProtectionDelay;
      // console.log(`监控帧率：当前1s内平均帧率为:${avg1Sec},3s内平均帧率为:${avg3Sec}，是否在恢复保护期:${isInRecoveryProtection},是否小于推荐帧率阈值(${recommendedFrameRateThreshold})：${isAvg3SecBelowRecommended},当前1s帧率是否小于3s平均帧率阈值${frameRateDrop}`);
      // 如果帧率下降超过30% 或者 低于推荐帧率的30% 或者 三秒平均帧率低于推荐帧率的30%，启用低帧率模式
      if (
        (frameRateDrop > this.frameRateDropThreshold || isFrameRateBelowRecommended || isAvg3SecBelowRecommended) &&
        !isInRecoveryProtection
      ) {
        if (!this.enableLowFrameRateMode) {
          this.enableLowFrameRateMode = true;
          let reason = '';
          if (frameRateDrop > this.frameRateDropThreshold) {
            reason = `帧率下降${(frameRateDrop * 100).toFixed(1)}%`;
          } else if (isFrameRateBelowRecommended) {
            reason = `当前帧率低于推荐值${this.recommendedFrameRate}fps的30%(${recommendedFrameRateThreshold.toFixed(1)}fps)`;
          } else if (isAvg3SecBelowRecommended) {
            reason = `三秒平均帧率低于推荐值${this.recommendedFrameRate}fps的30%(${recommendedFrameRateThreshold.toFixed(1)}fps)`;
          }
          console.log(`检测到${reason}，启用低帧率模式`);
          console.log(`最近1秒平均帧率: ${avg1Sec.toFixed(1)}fps，之前3秒平均帧率: ${avg3Sec.toFixed(1)}fps`);
          console.log(`设备性能等级: ${this.devicePerformanceLevel}，推荐帧率: ${this.recommendedFrameRate}fps`);
        }
      } else if (
        frameRateDrop < this.frameRateDropThreshold * 0.5 &&
        avg1Sec >= recommendedFrameRateThreshold * 1.2 &&
        avg3Sec >= recommendedFrameRateThreshold * 1.2
      ) {
        // 如果帧率恢复到较好水平（1秒和3秒平均帧率都超过推荐帧率30%阈值的1.2倍），禁用低帧率模式
        if (this.enableLowFrameRateMode && !this.isUserEnablLowFrameRateMode) {
          this.enableLowFrameRateMode = false;
          this.lastRecoveryTime = currentTime; // 记录恢复时间
          console.log('帧率恢复正常，禁用低帧率模式，10秒内不再进入低帧率模式');
          console.log(`最近1秒平均帧率: ${avg1Sec.toFixed(1)}fps，之前3秒平均帧率: ${avg3Sec.toFixed(1)}fps`);
        }
      } else if (
        isInRecoveryProtection &&
        (frameRateDrop > this.frameRateDropThreshold || isFrameRateBelowRecommended || isAvg3SecBelowRecommended)
      ) {
        // 在保护期内检测到性能问题，但不进入低帧率模式
        // console.log(
        //   `检测到性能问题，但在恢复保护期内(剩余${(
        //     (this.recoveryProtectionDelay - (currentTime - this.lastRecoveryTime)) /
        //     1000
        //   ).toFixed(1)}秒)，暂不进入低帧率模式`,
        // );
      }
    }
  }
  // 获取性能等级
  getPerformanceLevel(): PerformanceLevel {
    return this.devicePerformanceLevel;
  }
  // 获取推荐帧率
  getRecommendedFrameRate(): number {
    return this.recommendedFrameRate;
  }
  // 获取是否启用低帧率模式
  isLowFrameRateMode(): boolean {
    return this.enableLowFrameRateMode;
  }
  // 重置低帧率模式
  resetLowFrameRateMode(): void {
    if (this.enableLowFrameRateMode) {
      this.lastRecoveryTime = Date.now(); // 记录恢复时间
    }
    this.enableLowFrameRateMode = false;
    this.isUserEnablLowFrameRateMode = false;
  }
  // 手动进入低帧率模式
  enterLowFrameRateMode(): void {
    if (!this.enableLowFrameRateMode) {
      this.enableLowFrameRateMode = true;
      this.isUserEnablLowFrameRateMode = true;
      console.log('手动进入低帧率模式');
      console.log(`设备性能等级: ${this.devicePerformanceLevel}，推荐帧率: ${this.recommendedFrameRate}fps`);
    }
  }
}
